export const LANGUAGE_FIELDS = {
  cultures: {
    fields: {
      fr: 'french_name',
      en: 'english_name',
      lat: 'latin_name',
    },
    labels: {
      fr: 'Français',
      en: 'Anglais',
      lat: 'Latin',
    },
    defaultLanguage: 'fr',
  },
  enemies: {
    fields: {
      fr: 'display_french_latin',
      en: 'display_english_latin',
      lat: 'display_latin_french',
    },
    // R80 (27 mai 26): show the secondary language between parentheses.
    labels: {
      fr: 'Français (Latin)',
      en: 'Anglais (Latin)',
      lat: 'Latin (Français)',
    },
    defaultLanguage: 'fr',
  },
};

// R37 (27 mai 26): client format for product names is
//   NAME (APPROVAL_NUMBER) [Emballages : pkg1, pkg2]
// Each piece is optional; if no approval number, drop the parens; if no
// packagings, drop the brackets.
const formatProductNameLabel = (option, source) => {
  const name = option.label;
  const reg = source?.approval_number;
  const pkgs = source?.packagings;
  const pkgList = Array.isArray(pkgs)
    ? pkgs.map((p) => (typeof p === 'object' ? p?.name : p)).filter(Boolean)
    : [];

  let out = name;
  if (reg) out += ` (${reg})`;
  if (pkgList.length > 0) {
    pkgList.sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }));
    out += ` [Emballages : ${pkgList.join(', ')}]`;
  }
  return out;
};

// R18 (27 mai 26): longer dropdowns load a bigger first page so keyboard
// arrow-down flows through more options before a page boundary (rc-select
// resets the active item to the top whenever the list length grows, so we
// reduce how often that happens without preloading the whole list).
const LONG_LIST_PAGE_SIZE = 75;

export const BASIC_FILTER_CONFIGS = [
  {
    key: 'product_names',
    labelKey: 'product|filters.Product name',
    field: 'name.raw',
    pageSize: LONG_LIST_PAGE_SIZE,
    searchFields: [
      { field: 'name.keyword', primary: true },
      {
        field: 'packagings.name.keyword',
        label: 'Emballage',
        sourceField: 'packagings',
      },
      {
        field: 'approval_number.keyword',
        label: "N° d'homologation",
        sourceField: 'approval_number',
      },
    ],
    includeSourceFields: ['packagings', 'approval_number'],
    formatOptionLabel: formatProductNameLabel,
  },
  {
    key: 'active_substance_names',
    labelKey: 'product|filters.Active substance name',
    field: 'active_substances.active_substance.name.raw',
    pageSize: LONG_LIST_PAGE_SIZE,
    // Backend flattens the nested paths into parallel arrays via
    // _navigate_path, so the frontend can treat them like the substance
    // page (clean, flat source fields).
    searchFields: [
      { field: 'active_substances.active_substance.name.keyword', primary: true },
      {
        field: 'active_substances.active_substance.cas_numbers.keyword',
        label: 'CAS',
        sourceField: 'active_substances.active_substance.cas_numbers',
      },
      {
        field: 'active_substances.active_substance.synonyms.keyword',
        label: 'Synonyme',
        sourceField: 'active_substances.active_substance.synonyms',
      },
    ],
    includeSourceFields: [
      'active_substances.active_substance.cas_numbers',
      'active_substances.active_substance.synonyms',
    ],
    formatOptionLabel: (option, source) => {
      const cas = source?.['active_substances.active_substance.cas_numbers'];
      const casList = Array.isArray(cas)
        ? cas.filter((v) => v != null && String(v).trim() !== '')
        : cas
          ? [cas]
          : [];
      if (casList.length === 0) return option.label;
      return `${option.label} (${casList.join(', ')})`;
    },
  },
  {
    key: 'activity_sectors',
    labelKey: 'product|filters.Activity sector',
    field: 'activities_sector',
    value_field: 'id',
    label_field: 'name',
  },
  {
    // R52 (27 mai 26): Approbation biologique must cascade like every
    // other filter — the standalone boolean_select bypassed the cross-field
    // aggregation context, so "Oui" stayed visible even when no bio
    // product remained in the current selection. Switching to the regular
    // cascading select (`type` dropped) routes through the same agg path
    // as Cultures/Enemies/etc. The `valueLabels` map turns the
    // true/false bucket keys into "Oui" / "Non".
    key: 'organic_approval',
    labelKey: 'product|filters.Organic approval',
    field: 'is_organic_approval',
    valueLabels: { true: 'Oui', false: 'Non', 1: 'Oui', 0: 'Non' },
    valueOrder: ['true', 'false', '1', '0'],
  },
  {
    key: 'cultures',
    labelKey: 'product|filters.Culture (from treatments)',
    field: 'cultures',
    value_field: 'id',
    label_field: 'french_name',
    pageSize: LONG_LIST_PAGE_SIZE,
    languageConfig: LANGUAGE_FIELDS.cultures,
    filters: { filter_hide_in_pc_ma_search: false, filter_show_in_customer_filter_filter_PC: true },
    // R55 (27 mai 26): synonym hits surface in grey under the culture name.
    searchFields: [
      { field: 'cultures.french_name.keyword', primary: true },
      { field: 'cultures.french_name_synonyms.keyword', label: 'Synonyme', sourceField: 'cultures.french_name_synonyms' },
      { field: 'cultures.english_name_synonyms.keyword', label: 'Synonyme', sourceField: 'cultures.english_name_synonyms' },
      { field: 'cultures.latin_name_synonyms.keyword', label: 'Synonyme', sourceField: 'cultures.latin_name_synonyms' },
    ],
    // Searched name follows the language toggle: searching the English culture
    // name ("Apple") in English mode must match. Default searchFields only
    // covers French.
    searchFieldsByLanguage: {
      fr: ['cultures.french_name.keyword'],
      en: ['cultures.english_name.keyword'],
      lat: ['cultures.latin_name.keyword'],
    },
    includeSourceFields: [
      'cultures.french_name_synonyms',
      'cultures.english_name_synonyms',
      'cultures.latin_name_synonyms',
    ],
  },
  {
    key: 'cultures_practices',
    labelKey: 'product|filters.Cultural practices',
    field: 'cultures.cultural_practices',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'enemies',
    labelKey: 'product|filters.Enemies',
    field: 'enemies',
    value_field: 'id',
    label_field: 'client_display_name',
    languageConfig: LANGUAGE_FIELDS.enemies,
    // Span 2 grid columns: the enemies header carries the tutorial button +
    // "Type d'affichage" + "Langues" controls, which need a wider cell to
    // sit on one line (so the input aligns with the rest of the row).
    gridSpan: 2,
    // R78 (27 mai 26): the enemies dropdown carries multiple languages at
    // once (display_french_latin, etc.) so the label is plural.
    langLabelKey: 'common|Langues',
    // R73/R83/R84/R85 (27 mai 26): two display modes for diseases. The
    // selected mode swaps which ES field is used as the option/tag label.
    displayModeConfig: {
      fields: {
        regroupement: 'display_name_regroupement',
        organisme: 'display_name_organisme',
      },
      // R82 + R85: the actual label field is the mode × language cross-product
      // so a selected enemy's name follows BOTH the Type-d'affichage toggle
      // and the language toggle.
      combinedLabelFields: {
        regroupement: {
          fr: 'display_regroupement_fr',
          en: 'display_regroupement_en',
          lat: 'display_regroupement_lat',
        },
        organisme: {
          fr: 'display_organisme_fr',
          en: 'display_organisme_en',
          lat: 'display_organisme_lat',
        },
      },
      labels: {
        regroupement: 'Liste des maladies par regroupement',
        organisme: 'Liste des maladies par organisme',
      },
      descriptions: {
        regroupement: 'nom commun regroupant tous les organismes',
        organisme: "nom commun d'un organisme",
      },
      defaultDisplayMode: 'regroupement',
      toggleLabel: "Type d'affichage",
    },
    // R75 (27 mai 26): "Ennemi à afficher sur le site client (présence
    // confirmée au Québec)" must be on. Maps to the Enemy model's
    // `confirmed_presence_in_quebec` boolean.
    filters: { filter_confirmed_presence_in_quebec: true },
    // R77 (27 mai 26): synonym hits surface in grey under the enemy name.
    searchFields: [
      { field: 'enemies.client_display_name.keyword', primary: true },
      { field: 'enemies.french_name_synonyms.keyword', label: 'Synonyme', sourceField: 'enemies.french_name_synonyms' },
      { field: 'enemies.english_name_synonyms.keyword', label: 'Synonyme', sourceField: 'enemies.english_name_synonyms' },
      { field: 'enemies.latin_name_synonyms.keyword', label: 'Synonyme', sourceField: 'enemies.latin_name_synonyms' },
    ],
    // The searched name must follow the language toggle. Default `searchFields`
    // only covers French (client_display_name = "Nom français (latin)"), so
    // searching "Alfalfa" in English mode found nothing. Each language searches
    // its own displayed name + the latin shown in parentheses.
    searchFieldsByLanguage: {
      fr: ['enemies.client_display_name.keyword'],
      en: ['enemies.english_name.keyword', 'enemies.latin_name.keyword'],
      lat: ['enemies.latin_name.keyword', 'enemies.french_name.keyword'],
    },
    includeSourceFields: [
      'enemies.french_name_synonyms',
      'enemies.english_name_synonyms',
      'enemies.latin_name_synonyms',
    ],
    infoBulle: {
      title: 'product|filters.enemiesDisplayMode.title',
      content: 'product|filters.enemiesDisplayMode.content',
    },
    tutorialSlotKey: 'product-search-enemies',
  },
];

export const AGRONOMIC_FILTER_CONFIGS = [
  {
    key: 'chemical_families',
    labelKey: 'product|filters.Chemical families or biological families',
    field: 'pesticide_group_associations.chemical_family',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'pesticide_groups',
    labelKey: 'product|filters.Pesticide groups',
    field: 'pesticide_group_associations.pesticide_group',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_categories',
    labelKey: 'product|filters.Pesticide categories',
    field: 'active_substances.active_substance.pesticide_categories',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'arla_culture_groups',
    labelKey: 'product|filters.ARLA Culture Groups',
    field: 'cultures.arla_culture_group',
    value_field: 'id',
    label_field: 'group',
  },
  {
    key: 'product_types',
    labelKey: 'product|filters.Product types',
    field: 'product_types',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'activity_types',
    labelKey: 'product|filters.Activity types',
    field: 'activity_type_filters',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'application_types',
    labelKey: 'product|filters.Application Types',
    field: 'application_types',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'allergens',
    labelKey: 'product|filters.Allergens',
    field: 'allergens',
    value_field: 'id',
    label_field: 'name',
  },
];

// R7 (27 mai 26): same scheme as the MA page — symbol buckets are integers
// (1-6 / 1-5 / 1-4 depending on the symbol). Map to French labels per spec
// rules 104/106/108/110/112/114/116 and force "least → worst" order.
const SYMBOL_LABELS_6 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Léger',
  4: 'Modéré',
  5: 'Élevé',
  6: 'Extrêmement élevé',
};
const SYMBOL_ORDER_6 = [1, 2, 3, 4, 5, 6];

const SYMBOL_LABELS_5 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Modéré',
  4: 'Élevé',
  5: 'Extrêmement élevé',
};
const SYMBOL_ORDER_5 = [1, 2, 3, 4, 5];

const SYMBOL_LABELS_4 = {
  1: 'Aucun',
  2: 'Faible',
  3: 'Modéré',
  4: 'Élevé',
};
const SYMBOL_ORDER_4 = [1, 2, 3, 4];

export const HEALTH_FILTER_CONFIGS = [
  {
    key: 'acute_toxicity',
    labelKey: 'product|filters.Acute toxicity',
    field: 'symbols.health.acute_toxicity_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'long_term_effects',
    labelKey: 'product|filters.Long term effects',
    field: 'symbols.health.long_term_effect_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_5,
    valueOrder: SYMBOL_ORDER_5,
  },
];

export const ENVIRONMENT_FILTER_CONFIGS = [
  {
    key: 'fish_or_daphnia',
    labelKey: 'product|filters.Fish or daphnia',
    field: 'symbols.environment.fishes_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'birds',
    labelKey: 'product|filters.Birds',
    field: 'symbols.environment.birds_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_6,
    valueOrder: SYMBOL_ORDER_6,
  },
  {
    key: 'bees',
    labelKey: 'product|filters.Bees',
    field: 'symbols.environment.bees_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
  {
    key: 'persistence',
    labelKey: 'product|filters.Persistence',
    field: 'symbols.environment.persistence_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
  {
    key: 'leaching_potential',
    labelKey: 'product|filters.Leaching potential',
    field: 'symbols.environment.leaching_symbol.calculated_value',
    valueLabels: SYMBOL_LABELS_4,
    valueOrder: SYMBOL_ORDER_4,
  },
];

export const BASIC_FILTER_KEYS = BASIC_FILTER_CONFIGS.map((c) => c.key);
export const EXTRA_FILTER_KEYS = [
  ...AGRONOMIC_FILTER_CONFIGS.map((c) => c.key),
  ...HEALTH_FILTER_CONFIGS.map((c) => c.key),
  ...ENVIRONMENT_FILTER_CONFIGS.map((c) => c.key),
];

// Union of every configurable filter — used by FilterSection to compute the
// cross-field facet context passed to each ElasticsearchSelect.
export const ALL_FILTER_CONFIGS = [
  ...BASIC_FILTER_CONFIGS,
  ...AGRONOMIC_FILTER_CONFIGS,
  ...HEALTH_FILTER_CONFIGS,
  ...ENVIRONMENT_FILTER_CONFIGS,
];

export const DOCUMENT_TYPE = 'ProductDocument';